// src/components/messages/MessageInbox.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useHOA } from '../../contexts/HOAContext';
import { messagingService } from '../../services/messagingService';
import MessageThread from './MessageThread';
import NewMessageForm from './NewMessageForm';

function formatRelativeTime(ts) {
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const TYPE_BADGE = {
  acc:          { label: 'ACC Application',       className: 'bg-amber-100 text-amber-700' },
  general:      { label: 'General',               className: 'bg-sky-100 text-sky-700' },
  announcement: { label: 'Announcement',          className: 'bg-forest-100 text-forest-700' },
};

const MessageInbox = ({ showNewForm: showNewFormProp = false }) => {
  const { impersonating, user, effectiveRole, isLoading: authLoading } = useAuth();
  const { hoa, isLoading: hoaLoading } = useHOA();
  const [threads, setThreads] = useState([]);
  const [readMap, setReadMap] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const threadIdParam = searchParams.get('thread');
  const [showNew, setShowNew] = useState(showNewFormProp);

  const loadThreads = useCallback(async () => {
    if (authLoading || hoaLoading) {
      setLoading(true);
      return;
    }

    if (!hoa?.id || !user?.id) {
      setLoading(false);
      setLoadError('Inbox context is not ready. Please refresh or sign in again.');
      return;
    }

    setLoading(true);
    setLoadError('');
    try {
      const targetUserId = impersonating ? impersonating.id : user?.id;
      const targetRole = effectiveRole || user?.portalRole || 'member';
      const currentUserProfileName = impersonating ? (impersonating.full_name || impersonating.name) : (user?.full_name || user?.name);

      const [allThreads, r] = await Promise.all([
        messagingService.getThreads({ hoaId: hoa.id }),
        messagingService.getReadThreadIds(targetUserId),
      ]);

      const filteredThreads = allThreads.filter(thread => {
        const isWelcome = thread.acc_details?.is_welcome === true;
        
        // Welcome Messages ONLY go to their designated recipient
        if (isWelcome) {
          const mappedId = thread.acc_details?.target_user_id || thread.acc_details?.recipient_id;
          if (mappedId) return mappedId === targetUserId;

          // Fallback heuristic: check if recipient's first name is in the subject
          const firstName = currentUserProfileName ? currentUserProfileName.split(' ')[0] : 'xx_no_match';
          if (thread.subject?.includes(firstName)) return true;

          // Final fallback
          return thread.created_by === targetUserId;
        }

        // Non-Welcome Messages:
        // Members see announcements + messages they created
        if (targetRole === 'member') {
          return thread.type === 'announcement' || thread.created_by === targetUserId;
        }
        
        // Staff see all normal messages
        return true;
      });

      setThreads(filteredThreads);
      setReadMap(r);
    } catch (err) {
      console.error('Failed to load threads:', err);
      setLoadError(err?.message || 'Inbox could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [authLoading, hoaLoading, effectiveRole, hoa?.id, impersonating, user]);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  // Realtime: refresh thread list on new post
  useEffect(() => {
    const channel = messagingService.subscribeToNewPosts(() => {
      loadThreads();
    });
    return () => messagingService.unsubscribe(channel);
  }, [loadThreads]);

  const activeThread = useMemo(() => {
    if (!threadIdParam || !threads.length) return null;
    return threads.find(t => t.id === threadIdParam) || null;
  }, [threadIdParam, threads]);

  if (activeThread) {
    return (
      <MessageThread
        thread={activeThread}
        onBack={() => { 
          searchParams.delete('thread');
          setSearchParams(searchParams);
          loadThreads(); 
        }}
      />
    );
  }

  return (
    <section className="bg-section-bg p-6 rounded-[2.5rem] space-y-5 border border-amber-100/30">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-primary-dark text-xl">Inbox</h3>
        <button
          onClick={() => setShowNew(true)}
          className="flex items-center gap-2 px-5 py-2.5 bg-secondary text-white text-sm font-bold rounded-full hover:brightness-110 active:scale-95 transition-all"
        >
          <span className="material-symbols-outlined text-[18px]">add_circle</span>
          New Message
        </button>
      </div>

      {/* New message form */}
      <AnimatePresence>
        {showNew && (
          <>
            <motion.div
              key="new-message-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowNew(false)}
              className="fixed inset-0 z-[70] bg-primary-dark/50 backdrop-blur-sm md:hidden"
            />
            <NewMessageForm
              onClose={() => setShowNew(false)}
              onSent={() => { setShowNew(false); loadThreads(); }}
            />
          </>
        )}
      </AnimatePresence>

      {/* Thread list */}
      {loadError ? (
        <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl warm-tactile-shadow border border-amber-200/60 text-center px-8">
          <div className="w-16 h-16 rounded-2xl bg-section-bg flex items-center justify-center mb-6 warm-tactile-shadow">
            <span className="material-symbols-outlined text-4xl text-secondary">error</span>
          </div>
          <h4 className="text-lg font-bold text-primary-dark mb-2">Could not load inbox</h4>
          <p className="text-sm text-slate-500 font-medium max-w-xs mb-6">{loadError}</p>
          <button
            onClick={loadThreads}
            className="bg-secondary text-white px-6 py-2.5 rounded-full text-sm font-bold hover:brightness-110 active:scale-95 transition-all"
          >
            Retry
          </button>
        </div>
      ) : loading ? (
        <div className="flex justify-center py-12 bg-white rounded-2xl border border-amber-200/60">
          <span className="material-symbols-outlined text-3xl text-secondary/50 animate-spin">progress_activity</span>
        </div>
      ) : threads.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl warm-tactile-shadow border border-amber-200/60 text-center px-8">
          <div className="w-16 h-16 rounded-2xl bg-section-bg flex items-center justify-center mb-6 warm-tactile-shadow">
            <span className="material-symbols-outlined text-4xl text-secondary">chat_bubble</span>
          </div>
          <h4 className="text-lg font-bold text-primary-dark mb-2">Your inbox is empty</h4>
          <p className="text-sm text-slate-500 font-medium max-w-xs leading-relaxed">Messages from the board and management will appear here.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {threads.map(thread => {
            const lastRead = readMap.get(thread.id);
            const isWelcome = thread.acc_details?.is_welcome === true;
            let isSentByMe = thread.created_by === (impersonating ? impersonating.id : user?.id);
            if (isWelcome) isSentByMe = false;
            const updatedMs = new Date(thread.updated_at).getTime();
            const createdMs = new Date(thread.created_at).getTime();
            const hasReply = updatedMs - createdMs > 2000;
            const isUnread = isSentByMe
              ? hasReply && (!lastRead || updatedMs > lastRead)
              : !lastRead || updatedMs > lastRead;
            const badge = TYPE_BADGE[thread.type] || TYPE_BADGE.general;
            // Welcome threads are owned by the recipient (thread.created_by), but the
            // actual message author is the admin who sent it. acc_details.sender_name
            // carries the real sender for these threads; thread.creator (joined via
            // created_by) would incorrectly resolve to the recipient themselves.
            const displayName = isWelcome
              ? (thread.acc_details?.sender_name || 'HOA Team')
              : (thread.creator?.full_name || 'Unknown');

            return (
              <motion.button
                key={thread.id}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={() => {
                  if (!impersonating) {
                    messagingService.markRead(thread.id, user.id);
                  }
                  searchParams.set('thread', thread.id);
                  setSearchParams(searchParams);
                  window.dispatchEvent(new Event('messages_read'));
                  
                  // Optimistic update of read store to timestamp now
                  setReadMap(prev => {
                    const next = new Map(prev);
                    next.set(thread.id, Date.now());
                    return next;
                  });
                }}
                className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${
                  isUnread
                    ? 'bg-white border-secondary/40 warm-tactile-shadow'
                    : 'bg-white border-amber-200/60 hover:border-secondary/40 hover:bg-main-bg/40'
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Unread dot */}
                  <span className={`mt-2 w-2 h-2 rounded-full flex-shrink-0 transition-colors ${
                    isUnread ? 'bg-secondary' : 'bg-transparent'
                  }`} />

                  {/* Avatar */}
                  {thread.type === 'announcement' ? (
                    <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0">
                      <span className="material-symbols-outlined text-[18px] text-secondary">campaign</span>
                    </div>
                  ) : (
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold ${
                      isUnread ? 'bg-secondary text-white' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {(displayName || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                  )}

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    {/* Row 1: sender name + badges + time */}
                    <div className="flex items-center justify-between gap-2">
                      <span className={`font-display text-base font-semibold truncate ${isUnread ? 'text-slate-900' : 'text-slate-600'}`}>
                        {thread.type === 'announcement' ? 'HOA Management' : displayName}
                      </span>
                      <span className="text-[11px] text-slate-400 flex-shrink-0">
                        {formatRelativeTime(thread.updated_at)}
                      </span>
                    </div>

                    {/* Row 2: address (from ACC details) or email */}
                    {(thread.acc_details?.address || thread.creator?.email) && (
                      <p className="text-xs text-secondary font-medium mt-0.5 truncate">
                        {thread.acc_details?.address || thread.creator?.email}
                      </p>
                    )}

                    {/* Row 3: subject + type badge */}
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className={`text-sm truncate ${isUnread ? 'text-slate-800 font-semibold' : 'text-slate-400'}`}>
                        {thread.subject}
                      </span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold flex-shrink-0 ${badge.className}`}>
                        {badge.label}
                      </span>
                      {thread.type === 'acc' && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-500 flex-shrink-0">
                          {thread.status.replace(/_/g, ' ')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default MessageInbox;
